import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { behavioralAPI } from '../lib/api';
import { toast } from 'sonner';

const BG = '#F5FAFC';
const TERRA = '#0076A3';
const INK = '#002A38';

const shell = { minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', fontFamily: 'DM Sans, sans-serif' };
const card = { background: '#fff', border: '1px solid #E7E5E4', borderRadius: 20, padding: '2rem', maxWidth: 640, width: '100%', boxShadow: '0 8px 30px rgba(28,25,23,0.05)' };
const h1 = { fontFamily: 'Cabinet Grotesk', fontSize: '1.75rem', fontWeight: 700, color: INK, letterSpacing: '-0.02em', lineHeight: 1.15 };
const bodyText = { fontSize: '0.95rem', color: '#57534E', lineHeight: 1.7 };
const btn = { background: TERRA, color: '#fff', border: 'none', borderRadius: 999, padding: '0.9rem 1.75rem', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', width: '100%', transition: 'background-color 0.2s, transform 0.2s' };

function Loading({ label }) {
  return (
    <div style={shell}>
      <div style={{ ...card, textAlign: 'center' }} data-testid="behavioral-loading">
        <div className="status-dot status-processing" style={{ display: 'inline-block', width: 16, height: 16, marginBottom: '1rem' }} />
        <p style={bodyText}>{label || 'Loading…'}</p>
      </div>
    </div>
  );
}

export default function BehavioralTakePage() {
  const { token } = useParams();
  const [step, setStep] = useState('loading'); // loading|landing|instructions|assessment|submitting|results|error
  const [meta, setMeta] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answered, setAnswered] = useState(new Set());
  const [idx, setIdx] = useState(0);
  const [most, setMost] = useState(null);
  const [least, setLeast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);
  const [errMsg, setErrMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await behavioralAPI.take(token);
      setMeta(data);
      setQuestions(data.questions || []);
      setAnswered(new Set(data.answered_question_numbers || []));
      if (data.status === 'completed') {
        const r = await behavioralAPI.takeResults(token);
        setResults(r.data.results);
        setStep('results');
      } else {
        setStep('landing');
      }
    } catch (e) {
      setErrMsg(e.response?.data?.detail || 'This assessment link is invalid or has expired.');
      setStep('error');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const beginAssessment = async () => {
    try {
      await behavioralAPI.takeStart(token);
      const ans = new Set(answered);
      const firstUnanswered = (questions.findIndex((q) => !ans.has(q.number)));
      setIdx(firstUnanswered === -1 ? 0 : firstUnanswered);
      setMost(null); setLeast(null);
      setStep('assessment');
    } catch (e) {
      toast.error('Could not start the assessment. Please try again.');
    }
  };

  const pickMost = (w) => { setMost(w); if (least === w) setLeast(null); };
  const pickLeast = (w) => { setLeast(w); if (most === w) setMost(null); };

  const continueNext = async () => {
    if (!most || !least || most === least) return;
    const q = questions[idx];
    setSaving(true);
    try {
      await behavioralAPI.takeResponse(token, { question_number: q.number, most_word: most, least_word: least });
      const nextAnswered = new Set(answered); nextAnswered.add(q.number); setAnswered(nextAnswered);
      if (idx + 1 >= questions.length) {
        setStep('submitting');
        const r = await behavioralAPI.takeSubmit(token);
        setResults(r.data.results);
        setStep('results');
      } else {
        setIdx(idx + 1); setMost(null); setLeast(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save your answer. Please try again.');
      if (step === 'submitting') setStep('assessment');
    } finally {
      setSaving(false);
    }
  };

  if (step === 'loading') return <Loading label="Loading your assessment…" />;
  if (step === 'submitting') return <Loading label="Generating your profile…" />;

  if (step === 'error') {
    return (
      <div style={shell}>
        <div style={{ ...card, textAlign: 'center' }} data-testid="behavioral-error">
          <h1 style={h1}>Assessment unavailable</h1>
          <p style={{ ...bodyText, marginTop: '0.75rem' }}>{errMsg}</p>
        </div>
      </div>
    );
  }

  // ── Landing / intro ──
  if (step === 'landing') {
    return (
      <div style={shell}>
        <div style={card} data-testid="behavioral-landing">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#FFF3EC', color: TERRA, borderRadius: 999, padding: '5px 14px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>
            CHIRE · Psychometric Assessment
          </div>
          <h1 style={h1}>Hi {meta?.candidate_name?.split(' ')[0] || 'there'}, let's discover your working style</h1>
          <p style={{ ...bodyText, marginTop: '1rem' }}>
            This short assessment looks at <strong>how you naturally prefer to work</strong> across four psychometric styles — how you take charge, connect with people, keep things steady, and handle detail.
            {meta?.job_title ? <> It's part of the process for the <strong>{meta.job_title}</strong> role.</> : null}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', margin: '1.5rem 0' }}>
            {[['~8–10 min', 'to complete'], ['24 questions', 'quick word choices'], ['No right answers', 'just be honest'], ['Private', 'shared only with the hiring team']].map(([a, b]) => (
              <div key={a} style={{ background: '#EDF4F7', borderRadius: 12, padding: '0.9rem' }}>
                <div style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 700, color: INK, fontSize: '0.95rem' }}>{a}</div>
                <div style={{ fontSize: '0.75rem', color: '#78716C' }}>{b}</div>
              </div>
            ))}
          </div>
          <p style={{ ...bodyText, fontSize: '0.85rem', marginBottom: '1.25rem' }}>
            There are <strong>no right or wrong answers</strong> — go with your gut. The most honest response gives the most useful result.
          </p>
          <button style={btn} data-testid="behavioral-landing-continue" onClick={() => setStep('instructions')}
            onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.99)')} onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}>
            Get started
          </button>
        </div>
      </div>
    );
  }

  // ── Instructions with worked example ──
  if (step === 'instructions') {
    const example = ['Bold', 'Friendly', 'Patient', 'Precise'];
    return (
      <div style={shell}>
        <div style={card} data-testid="behavioral-instructions">
          <h1 style={{ ...h1, fontSize: '1.4rem' }}>How it works</h1>
          <p style={{ ...bodyText, marginTop: '0.75rem' }}>
            Each screen shows <strong>four words</strong>. For every screen, pick the <strong>one word that is MOST like you</strong> and the <strong>one word that is LEAST like you</strong>. That's it.
          </p>
          <div style={{ background: '#EDF4F7', borderRadius: 14, padding: '1.1rem', margin: '1.25rem 0' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#78716C', marginBottom: '0.75rem' }}>Worked example</div>
            {example.map((w, i) => (
              <div key={w} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: i < 3 ? '1px solid #E7E5E4' : 'none' }}>
                <span style={{ fontSize: '0.95rem', color: INK, fontWeight: 500 }}>{w}</span>
                <span style={{ display: 'flex', gap: 6 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: w === 'Bold' ? TERRA : '#fff', color: w === 'Bold' ? '#fff' : '#A8A29E', border: '1px solid #E7E5E4' }}>Most</span>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: w === 'Precise' ? '#44403C' : '#fff', color: w === 'Precise' ? '#fff' : '#A8A29E', border: '1px solid #E7E5E4' }}>Least</span>
                </span>
              </div>
            ))}
            <p style={{ fontSize: '0.8rem', color: '#78716C', marginTop: '0.75rem', marginBottom: 0 }}>Here we chose <strong>Bold</strong> as most like us and <strong>Precise</strong> as least. Your answers save automatically, and you can't go back once you continue — so trust your first instinct.</p>
          </div>
          <button style={btn} data-testid="behavioral-instructions-start" onClick={beginAssessment}>Start the assessment</button>
        </div>
      </div>
    );
  }

  // ── Results (candidate-facing, plain language, no numbers) ──
  if (step === 'results') {
    const r = results || {};
    return (
      <div style={{ ...shell, alignItems: 'flex-start', paddingTop: '3rem', paddingBottom: '3rem' }}>
        <div style={{ ...card, maxWidth: 680 }} data-testid="behavioral-results">
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: TERRA }}>Your psychometric profile</div>
            <h1 style={{ ...h1, marginTop: '0.5rem' }}>{r.headline}</h1>
            {r.style_label && <div style={{ display: 'inline-block', marginTop: '0.75rem', background: '#FFF3EC', color: TERRA, borderRadius: 999, padding: '6px 16px', fontWeight: 700, fontSize: '0.85rem' }}>{r.style_label}</div>}
          </div>
          <p style={{ ...bodyText }}>{r.summary}</p>

          {[['What you bring', r.strengths, '#7FB08A'], ['Where to keep growing', r.growth_areas, '#E39A3B'], ['How you work best', r.working_style_tips, '#7E8FB5']].map(([title, items, col]) => (
            <div key={title} style={{ marginTop: '1.5rem' }}>
              <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.05rem', fontWeight: 700, color: INK, marginBottom: '0.6rem' }}>{title}</h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {(items || []).map((it) => (
                  <li key={it} style={{ display: 'flex', gap: 10, fontSize: '0.9rem', color: '#44403C', lineHeight: 1.5 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 999, background: col, marginTop: 7, flexShrink: 0 }} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div style={{ marginTop: '1.75rem', padding: '1rem', background: '#EDF4F7', borderRadius: 12, fontSize: '0.8rem', color: '#78716C', textAlign: 'center' }}>
            {meta?.self_serve
              ? 'Thanks for completing your assessment — we\u2019ve emailed your full report (two PDFs) to your inbox. You can close this page.'
              : 'Thanks for completing your assessment — your results have been shared with the hiring team. You can close this page.'}
          </div>
        </div>
      </div>
    );
  }

  // ── Assessment (forced choice) ──
  const q = questions[idx] || { words: [] };
  const progress = Math.round(((idx) / questions.length) * 100);
  const canContinue = most && least && most !== least;
  const isLast = idx + 1 >= questions.length;

  return (
    <div style={{ ...shell, alignItems: 'flex-start', paddingTop: '2rem' }}>
      <div style={{ ...card, maxWidth: 620 }} data-testid="behavioral-assessment">
        {/* progress */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#78716C', marginBottom: 6 }}>
            <span data-testid="behavioral-progress-label">Question {idx + 1} of {questions.length}</span>
            <span>{progress}%</span>
          </div>
          <div style={{ height: 8, background: '#F1EDE8', borderRadius: 999, overflow: 'hidden' }}>
            <div data-testid="behavioral-progress-bar" style={{ width: `${progress}%`, height: '100%', background: TERRA, borderRadius: 999, transition: 'width 0.4s ease' }} />
          </div>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#78716C', marginBottom: '0.25rem' }}>Pick the word that is <strong style={{ color: TERRA }}>most</strong> like you and the one that is <strong style={{ color: '#44403C' }}>least</strong> like you.</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', margin: '1.25rem 0 1.5rem' }}>
          {q.words.map((w) => {
            const isMost = most === w; const isLeast = least === w;
            const rowBg = isMost ? 'rgba(240, 80, 0,0.08)' : isLeast ? 'rgba(68,64,60,0.06)' : '#fff';
            const rowBorder = isMost ? TERRA : isLeast ? '#44403C' : '#E7E5E4';
            return (
              <div key={w} data-testid={`behavioral-word-row-${w}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '0.75rem 0.9rem', border: `1.5px solid ${rowBorder}`, borderRadius: 12, background: rowBg, transition: 'all 0.15s' }}>
                <span style={{ fontSize: '1rem', color: INK, fontWeight: 600 }}>{w}</span>
                <span style={{ display: 'flex', gap: 8 }}>
                  <button type="button" data-testid={`behavioral-most-${w}`} onClick={() => pickMost(w)}
                    style={{ fontSize: '0.72rem', fontWeight: 700, padding: '6px 14px', borderRadius: 999, cursor: 'pointer', border: `1.5px solid ${isMost ? TERRA : '#E7E5E4'}`, background: isMost ? TERRA : '#fff', color: isMost ? '#fff' : '#A8A29E', transition: 'all 0.15s' }}>
                    Most
                  </button>
                  <button type="button" data-testid={`behavioral-least-${w}`} onClick={() => pickLeast(w)}
                    style={{ fontSize: '0.72rem', fontWeight: 700, padding: '6px 14px', borderRadius: 999, cursor: 'pointer', border: `1.5px solid ${isLeast ? '#44403C' : '#E7E5E4'}`, background: isLeast ? '#44403C' : '#fff', color: isLeast ? '#fff' : '#A8A29E', transition: 'all 0.15s' }}>
                    Least
                  </button>
                </span>
              </div>
            );
          })}
        </div>

        <button
          style={{ ...btn, opacity: canContinue && !saving ? 1 : 0.5, cursor: canContinue && !saving ? 'pointer' : 'not-allowed' }}
          disabled={!canContinue || saving}
          data-testid="behavioral-continue-button"
          onClick={continueNext}
        >
          {saving ? 'Saving…' : isLast ? 'Finish & see my profile' : 'Continue'}
        </button>
        <p style={{ textAlign: 'center', fontSize: '0.72rem', color: '#A8A29E', marginTop: '0.9rem', marginBottom: 0 }}>Your answers save automatically. You can't return to a previous question.</p>
      </div>
    </div>
  );
}
